import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { PageHeader, Card } from '../components/ui';
import { api } from '../lib/api';

interface AuditDetail {
  id: string;
  actorId: string | null;
  actorEmail: string | null;
  entity: string;
  entityId: string | null;
  action: string;
  summary: string;
  before: unknown;
  after: unknown;
  createdAt: string;
}

export default function AuditDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery<AuditDetail | { error: string }>({
    queryKey: ['admin', 'audit', id],
    queryFn: () => api(`/api/admin/audit/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <div style={{ padding: 40, color: 'var(--text-3)' }}>Cargando…</div>;
  if (!data || 'error' in data) {
    return (
      <div>
        <PageHeader kicker="AUDITORÍA" title="Evento no encontrado" />
        <Link to="/audits" style={{ color: 'var(--accent)', textDecoration: 'none' }}>← Volver</Link>
      </div>
    );
  }

  const a = data;
  const beforeJson = a.before != null ? JSON.stringify(a.before, null, 2) : null;
  const afterJson = a.after != null ? JSON.stringify(a.after, null, 2) : null;

  return (
    <div>
      <Link to="/audits" style={{ color: 'var(--text-2)', fontSize: 13, textDecoration: 'none' }}>← Auditoría</Link>
      <PageHeader
        kicker={`${a.entity.toUpperCase()} · ${a.action.toUpperCase()}`}
        title={a.summary || `(${a.entity}.${a.action})`}
        sub={`${a.actorEmail ?? '—'} · ${new Date(a.createdAt).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'medium' })}`}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 16, marginBottom: 20 }}>
        <Stat label="ENTIDAD" value={a.entity} mono />
        <Stat label="ACCIÓN" value={a.action} mono />
        <Stat label="ENTITY ID" value={a.entityId ?? '—'} mono />
        <Stat label="ACTOR" value={a.actorEmail ?? '—'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.14em' }}>ANTES</div>
          {beforeJson ? (
            <pre style={preStyle}>{beforeJson}</pre>
          ) : (
            <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '8px 0' }}>—</div>
          )}
        </Card>
        <Card>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.14em' }}>DESPUÉS</div>
          {afterJson ? (
            <pre style={preStyle}>{afterJson}</pre>
          ) : (
            <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '8px 0' }}>—</div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.12em' }}>{label}</div>
      <div className={mono ? 'mono' : ''} style={{ marginTop: 6, fontSize: 13, fontWeight: 500, wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}

const preStyle: React.CSSProperties = {
  marginTop: 12, padding: 12, background: 'var(--surface-2)', borderRadius: 8,
  fontSize: 11, lineHeight: 1.5, fontFamily: 'JetBrains Mono, monospace',
  overflow: 'auto', maxHeight: 500, color: 'var(--text-2)', whiteSpace: 'pre-wrap',
};
